import type { AnnotationLabelT, AnnotationUnderUserReviewT } from '../../../../../common/types/types';
import { getCanvasContextWithImage } from '../../../../labelling-v2/utils/get-canvas-context';
import { ANNOTATION_TYPE, BITMAP_HASH_SAMPLE_RATE, CANVAS_OPERATION, CENTROID_DENSITY_SAMPLE_RATE, CENTROID_DENSITY_THRESHOLD, CENTROID_EARLY_EXIT_SAMPLE_RATE, CENTROID_HIGH_DENSITY_SAMPLE_RATE, CENTROID_LOW_DENSITY_SAMPLE_RATE, CENTROID_SMALL_IMAGE_EARLY_EXIT_RATE, CENTROID_SMALL_IMAGE_THRESHOLD, CONTOUR_DIRECTIONS, getAnnotationOutlineAlpha, getAnnotationOutlineWidth, HASH_MULTIPLIER, MAX_OUTLINE_PATH_CACHE_SIZE, SEGMENTATION_ALPHA } from '../../../constants';
import type { MetadataT } from '../../../providers/stage-provider';
import type { AnnotationT, BrushAnnotationT, BrushDataT } from '../../../types';
import type { AnnotationPixelMap } from '../atoms/segmentation-atoms';
export const clearCanvas = (ctx: CanvasRenderingContext2D) => {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
};
export const removePoints = (ctx: CanvasRenderingContext2D, points: [number, number][]) => {
  for (let j = 0; j < points.length; j++) {
    ctx.beginPath();
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    ctx.clearRect(points[j][0], points[j][1], 1, 1);
    ctx.stroke();
    ctx.closePath();
  }
};
export const preProcessSAMPixelData = (originalData: ImageData) => {
  const updatedImageData = new ImageData(new Uint8ClampedArray(originalData.data), originalData.width, originalData.height);
  const data = updatedImageData.data;
  for (let i = 0; i < updatedImageData.data.length; i += 4) {
    // Remove all black pixels (R=0, G=0, B=0) that has some opacity (A>0)
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (data[i + 3] > 0 && data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0) {
      // Set alpha channel to 0
      data[i + 3] = 0;
    }
  }
  return updatedImageData;
};
export const base64ToImageData = async (imageUrl?: string, preProcessData?: (imageData: ImageData) => ImageData) => {
  if (!imageUrl) return null;
  const contextWithImage = await getCanvasContextWithImage(imageUrl);
  if (!contextWithImage) return null;
  const {
    ctx,
    canvas,
    cleanedUp
  } = contextWithImage;
  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const updatedImageData = preProcessData ? preProcessData(imageData) : imageData;
    return updatedImageData;
  } catch (error) {
    console.error('Failed to get image data:', error);
    return null;
  } finally {
    cleanedUp();
  }
};
export const applyColorToImageData = (imageData: ImageData, labelColor: string) => {
  const colorArray = rgbStringToArray(labelColor);
  for (let i = 0; i < imageData.data.length; i += 4) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (imageData.data[i + 3] > 0) {
      imageData.data[i] = colorArray[0];
      imageData.data[i + 1] = colorArray[1];
      imageData.data[i + 2] = colorArray[2];
      imageData.data[i + 3] = SEGMENTATION_ALPHA;
    }
  }
};

// Utility function to find annotation label and merge bitmaps
export const mergeAnnotationBitmap = (mergedBitmap: ImageData, bitmap: ImageData, title: string, annotationLabels: AnnotationLabelT[]): ImageData => {
  const annotationLabel = annotationLabels.find(label => label.title === title);

  /* passing selected annotation label to merge bitmaps if annotation label is not present in annotations.
  this would happen when first annotation of a new label is created. */
  const color = annotationLabel?.style?.fillColor || annotationLabel?.style?.strokeColor;
  if (color) {
    const mergedResult = mergeBitmaps(mergedBitmap, bitmap, color);
    return mergedResult.mergedBitmap;
  }
  return mergedBitmap;
};
const rgbStringToArray = (rgbString: string): [number, number, number] => {
  const match = rgbString.match(/\d+/g);
  const [r, g, b] = match?.map(value => parseInt(value, 10)) || [0, 0, 0];
  // @ts-expect-error todo(ts-migration) TS2322 Type 'number | undefined' is not assignable to type 'number'.
  return [r, g, b];
};
export const drawPoints = (ctx: CanvasRenderingContext2D, points: [number, number][], color: string) => {
  ctx.fillStyle = rgbToRgba(color);
  ctx.globalCompositeOperation = CANVAS_OPERATION.SOURCE_OVER;
  for (let j = 0; j < points.length; j++) {
    ctx.beginPath();
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    ctx.fillRect(points[j][0], points[j][1], 1, 1);
    ctx.stroke();
    ctx.closePath();
  }
};
export const erasePoints = (ctx: CanvasRenderingContext2D, points: [number, number][]) => {
  ctx.fillStyle = 'black';
  ctx.globalCompositeOperation = CANVAS_OPERATION.DESTINATION_OUT;
  for (let j = 0; j < points.length; j++) {
    ctx.beginPath();
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    ctx.fillRect(points[j][0], points[j][1], 1, 1);
    ctx.stroke();
    ctx.closePath();
  }
  ctx.globalCompositeOperation = CANVAS_OPERATION.SOURCE_OVER;
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  for (let j = 0; j < points.length; j++) {
    ctx.beginPath();
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    ctx.fillRect(points[j][0], points[j][1], 1, 1);
    ctx.stroke();
    ctx.closePath();
  }
};
const rgbToRgba = (color: string) => {
  let rgbaColor = color.replace(/rgb/i, 'rgba');
  rgbaColor = rgbaColor.replace(/\)/i, ',0.5)');
  return rgbaColor;
};
const NO_OF_ITEMS_PER_PIXEL = 4;
const ERASER_COLOR = 255;
export const getDrawnPoints = (ctx: CanvasRenderingContext2D) => {
  const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
  const data = imageData.data;
  const points: [number, number][] = [];
  const erasedPoints: [number, number][] = [];
  for (let i = 0; i < data.length + NO_OF_ITEMS_PER_PIXEL; i += NO_OF_ITEMS_PER_PIXEL) {
    const x = i / NO_OF_ITEMS_PER_PIXEL % ctx.canvas.width;
    const y = Math.floor(i / NO_OF_ITEMS_PER_PIXEL / ctx.canvas.width);
    if (data[i] === ERASER_COLOR && data[i + 1] === ERASER_COLOR && data[i + 2] === ERASER_COLOR) {
      erasedPoints.push([x, y]);
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    } else if (data[i] > 0 || data[i + 1] > 0 || data[i + 2] > 0) {
      points.push([x, y]);
    }
  }
  return {
    points,
    erasedPoints
  };
};
export const drawLine = ({
  ctx,
  point,
  size,
  color,
  eraserSelected,
  moveFirstPoint
}: {
  ctx: CanvasRenderingContext2D;
  point: [number, number];
  size: number;
  color: string;
  eraserSelected: boolean;
  moveFirstPoint?: [number, number];
}) => {
  if (moveFirstPoint) {
    ctx.beginPath();
    ctx.moveTo(moveFirstPoint[0], moveFirstPoint[1]);
  }
  ctx.strokeStyle = eraserSelected ? 'rgba(255,255,255,0.5)' : rgbToRgba(color);
  ctx.lineWidth = size;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalCompositeOperation = CANVAS_OPERATION.DESTINATION_OUT;
  ctx.lineTo(point[0], point[1]);
  ctx.stroke();
  ctx.globalCompositeOperation = CANVAS_OPERATION.SOURCE_OVER;
  ctx.lineTo(point[0], point[1]);
  ctx.stroke();
};
export const fillDrawing = (ctx: CanvasRenderingContext2D, color: string, eraserSelected?: boolean) => {
  ctx.globalCompositeOperation = CANVAS_OPERATION.DESTINATION_OUT;
  ctx.fillStyle = 'black';
  ctx.fill();
  ctx.globalCompositeOperation = CANVAS_OPERATION.SOURCE_OVER;
  ctx.fillStyle = eraserSelected ? 'rgba(255,255,255,0.5)' : rgbToRgba(color);
  ctx.fill();
};
export const drawLines = ({
  ctx,
  points,
  color,
  eraserSelected
}: {
  ctx: CanvasRenderingContext2D;
  points: {
    point: [number, number];
    size: number;
  }[];
  color: string;
  eraserSelected: boolean;
}) => {
  ctx.beginPath();
  points.forEach(segmentationPoint => {
    drawLine({
      ctx,
      point: segmentationPoint.point,
      size: segmentationPoint.size,
      color,
      eraserSelected
    });
  });
};
export const drawBitmap = (ctx: CanvasRenderingContext2D, bitmap: ImageData) => {
  ctx.putImageData(bitmap, 0, 0);
};
export const mergeBitmaps = (existingBitmap: ImageData, newBitmap: ImageData, color: string) => {
  const width = existingBitmap.width;
  const height = existingBitmap.height;
  const existingData = existingBitmap.data;
  const newData = newBitmap.data;
  const colorArray = rgbStringToArray(rgbToRgba(color));

  // Create two new ImageData objects for the composite and for the modified (remaining) annotation.
  const mergedBitmap = new ImageData(new Uint8ClampedArray(width * height * 4), width, height);
  const updatedExistingBitmap = new ImageData(new Uint8ClampedArray(existingBitmap.data), width, height);
  for (let i = 0; i < existingData.length; i += 4) {
    const newAlpha = newData[i + 3];
    const existingAlpha = existingData[i + 3];

    // @ts-expect-error todo(ts-migration) TS18048 'newAlpha' is possibly 'undefined'.
    if (newAlpha > 0) {
      // Overlapping/ Non overlapping: new annotation takes precedence.
      mergedBitmap.data[i] = colorArray[0];
      mergedBitmap.data[i + 1] = colorArray[1];
      mergedBitmap.data[i + 2] = colorArray[2];
      mergedBitmap.data[i + 3] = SEGMENTATION_ALPHA;

      // In either case, remove the pixel from the existing annotation in case of overlapping annotation.
      updatedExistingBitmap.data.set([0, 0, 0, 0], i);
      // @ts-expect-error todo(ts-migration) TS18048 'existingAlpha' is possibly 'undefined'.
    } else if (existingAlpha > 0) {
      // No new annotation here, so keep the existing pixel.
      // @ts-expect-error todo(ts-migration) TS2322 Type 'number | undefined' is not assignable to type 'number'.
      mergedBitmap.data[i] = existingData[i];
      // @ts-expect-error todo(ts-migration) TS2322 Type 'number | undefined' is not assignable to type 'number'.
      mergedBitmap.data[i + 1] = existingData[i + 1];
      // @ts-expect-error todo(ts-migration) TS2322 Type 'number | undefined' is not assignable to type 'number'.
      mergedBitmap.data[i + 2] = existingData[i + 2];
      mergedBitmap.data[i + 3] = SEGMENTATION_ALPHA;
      // updatedExistingBitmap remains unchanged at this pixel.
    }
  }
  return {
    mergedBitmap,
    updatedExistingBitmap
  };
};
export const imageDataToBase64String = (imageData: ImageData): string => {
  // Create a canvas element matching the ImageData dimensions
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  // Draw the ImageData onto the canvas
  ctx.putImageData(imageData, 0, 0);

  // Convert the canvas content to a base64-encoded PNG
  return canvas.toDataURL('image/png');
};

// Strips the footprint of a newly committed mask from every OTHER segmentation
// annotation's bitmap, so a pixel is never claimed by two masks at once. Used by
// every mask-commit path (brush/eraser paint, and point/box/text-prompt accept).
export const eraseMaskFromOtherAnnotations = (newMaskBitmap: ImageData, newMaskColor: string, otherSegmentationAnnotations: Record<string, BrushAnnotationT>, annotationBitmap: Record<string, ImageData> | undefined, excludeAnnotationId: string | undefined, updateAnnotation: (annotationId: string, annotation: AnnotationT, metadata?: MetadataT) => void, metadata?: MetadataT): void => {
  for (const [annotationId, annotation] of Object.entries(otherSegmentationAnnotations)) {
    if (annotationId === excludeAnnotationId || annotation.hidden) continue;
    const existingImageData = annotationBitmap?.[annotation.annotationLabel.title];
    if (!existingImageData) continue;
    const {
      updatedExistingBitmap
    } = mergeBitmaps(existingImageData, newMaskBitmap, newMaskColor);
    const modifiedSegmentationImageData = imageDataToBase64String(updatedExistingBitmap);
    if (annotation.data?.base64String !== modifiedSegmentationImageData) {
      invalidateOutlineCache(annotation.annotationLabel.title);
      updateAnnotation(annotationId, {
        ...annotation,
        data: {
          ...annotation.data,
          base64String: modifiedSegmentationImageData
        }
      }, metadata);
    }
  }
};
export const applyOpacityToImageData = (imageData: ImageData, alpha: number) => {
  const newImageData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
  for (let i = 0; i < newImageData.data.length; i += 4) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (newImageData.data[i + 3] > 0) {
      newImageData.data[i + 3] = alpha;
    }
  }
  return newImageData;
};
export const removeAndExtractErasedRegions = (imageData: ImageData) => {
  const updatedImageData = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
  const erasedImageData = new ImageData(new Uint8ClampedArray(imageData.width * imageData.height * 4), imageData.width, imageData.height);
  for (let i = 0; i < updatedImageData.data.length; i += 4) {
    if (updatedImageData.data[i] === ERASER_COLOR && updatedImageData.data[i + 1] === ERASER_COLOR && updatedImageData.data[i + 2] === ERASER_COLOR) {
      updatedImageData.data.set([0, 0, 0, 0], i);
      erasedImageData.data.set([ERASER_COLOR, ERASER_COLOR, ERASER_COLOR, ERASER_COLOR], i);
    }
  }
  return {
    updatedImageData,
    erasedImageData
  };
};
export const applyErasedBitmapMask = (erasedImageData: ImageData, annotationImageData: ImageData) => {
  const updatedImageData = new ImageData(new Uint8ClampedArray(annotationImageData.data), annotationImageData.width, annotationImageData.height);
  for (let i = 0; i < erasedImageData.data.length; i += 4) {
    if (erasedImageData.data[i + 3] === ERASER_COLOR && erasedImageData.data[i + 2] === ERASER_COLOR && erasedImageData.data[i + 1] === ERASER_COLOR && erasedImageData.data[i] === ERASER_COLOR) {
      updatedImageData.data.set([0, 0, 0, 0], i);
    }
  }
  return updatedImageData;
};
export const removeStaleAnnotationBitmaps = (currentBitmaps: Record<string, ImageData>, semanticAnnotations: Record<string, BrushAnnotationT>): Record<string, ImageData> => {
  const validAnnotationLabelTitles = Object.values(semanticAnnotations).map(annotation => annotation.annotationLabel.title);
  const newBitmaps: Record<string, ImageData> = {};
  Object.entries(currentBitmaps).forEach(([title, bitmap]) => {
    if (validAnnotationLabelTitles.includes(title)) {
      newBitmaps[title] = bitmap;
    }
  });
  return newBitmaps;
};
export const drawAnnotationOutline = (ctx: CanvasRenderingContext2D, bitmap: ImageData) => {
  ctx.beginPath();
  for (let y = 0; y < bitmap.height; y++) {
    for (let x = 0; x < bitmap.width; x++) {
      const index = (y * bitmap.width + x) * 4;
      const alpha = bitmap.data[index + 3];

      // @ts-expect-error todo(ts-migration) TS18048 'alpha' is possibly 'undefined'.
      if (alpha > 0) {
        const isEdge = x === 0 || y === 0 || x === bitmap.width - 1 || y === bitmap.height - 1 || y > 0 && bitmap.data[((y - 1) * bitmap.width + x) * 4 + 3] === 0 || y < bitmap.height - 1 && bitmap.data[((y + 1) * bitmap.width + x) * 4 + 3] === 0 || x > 0 && bitmap.data[(y * bitmap.width + x - 1) * 4 + 3] === 0 || x < bitmap.width - 1 && bitmap.data[(y * bitmap.width + x + 1) * 4 + 3] === 0;
        if (isEdge) {
          ctx.rect(x, y, 1, 1);
        }
      }
    }
  }
  ctx.stroke();
};
const outlinePathCache = new Map<string, Array<{
  x: number;
  y: number;
}>>();
const isOpaque = (bitmap: ImageData, x: number, y: number): boolean => {
  if (x < 0 || x >= bitmap.width || y < 0 || y >= bitmap.height) return false;
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  return bitmap.data[(y * bitmap.width + x) * 4 + 3] > 0;
};
const isEdgePixel = (bitmap: ImageData, x: number, y: number): boolean => {
  if (!isOpaque(bitmap, x, y)) return false;
  return !isOpaque(bitmap, x - 1, y) || !isOpaque(bitmap, x + 1, y) || !isOpaque(bitmap, x, y - 1) || !isOpaque(bitmap, x, y + 1);
};
const findBoundaryStartPoints = (bitmap: ImageData): Array<{
  x: number;
  y: number;
}> => {
  const startPoints: Array<{
    x: number;
    y: number;
  }> = [];
  const width = bitmap.width;
  const height = bitmap.height;
  for (let x = 0; x < width; x++) {
    if (isOpaque(bitmap, x, 0)) startPoints.push({
      x,
      y: 0
    });
    if (isOpaque(bitmap, x, height - 1)) startPoints.push({
      x,
      y: height - 1
    });
  }
  for (let y = 1; y < height - 1; y++) {
    if (isOpaque(bitmap, 0, y)) startPoints.push({
      x: 0,
      y
    });
    if (isOpaque(bitmap, width - 1, y)) startPoints.push({
      x: width - 1,
      y
    });
  }
  for (let y = 10; y < height; y += 10) {
    for (let x = 10; x < width; x += 10) {
      if (isOpaque(bitmap, x, y) && isEdgePixel(bitmap, x, y)) {
        startPoints.push({
          x,
          y
        });
      }
    }
  }
  return startPoints;
};
const traceContourFrom = (bitmap: ImageData, startX: number, startY: number, visited: Set<string>, edgePoints: Array<{
  x: number;
  y: number;
}>): void => {
  const stack = [{
    x: startX,
    y: startY
  }];
  const width = bitmap.width;
  const height = bitmap.height;
  while (stack.length > 0) {
    const current = stack.pop()!;
    const key = `${current.x},${current.y}`;
    if (visited.has(key)) continue;
    if (isEdgePixel(bitmap, current.x, current.y)) {
      visited.add(key);
      edgePoints.push(current);
      for (const dir of CONTOUR_DIRECTIONS) {
        const nextX = current.x + dir.dx;
        const nextY = current.y + dir.dy;
        const nextKey = `${nextX},${nextY}`;
        if (!visited.has(nextKey) && nextX >= 0 && nextX < width && nextY >= 0 && nextY < height && isOpaque(bitmap, nextX, nextY)) {
          stack.push({
            x: nextX,
            y: nextY
          });
        }
      }
    }
  }
};
export const generateBitmapContentHash = (bitmap: ImageData): string => {
  let hash = 0;
  let nonZeroCount = 0;
  let minX = bitmap.width;
  let maxX = 0;
  let minY = bitmap.height;
  let maxY = 0;

  // Include spatial distribution in hash for better detection of position/shape changes
  for (let i = 0; i < bitmap.data.length; i += BITMAP_HASH_SAMPLE_RATE) {
    const pixelIndex = i / 4;
    const horizontalIndex = pixelIndex % bitmap.width;
    const verticalIndex = Math.floor(pixelIndex / bitmap.width);

    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (bitmap.data[i + 3] > 0) {
      nonZeroCount++;
      minX = Math.min(minX, horizontalIndex);
      maxX = Math.max(maxX, horizontalIndex);
      minY = Math.min(minY, verticalIndex);
      maxY = Math.max(maxY, verticalIndex);

      // Include color and position in hash
      hash = hash * HASH_MULTIPLIER + (bitmap.data?.[i] ?? 0) + (bitmap.data?.[i + 1] ?? 0) + (bitmap.data?.[i + 2] ?? 0) + horizontalIndex + verticalIndex >>> 0;
    }
  }

  // Include bounding box dimensions in hash to detect shape changes
  const width = maxX - minX;
  const height = maxY - minY;
  return `${nonZeroCount}-${hash}-${bitmap.width}x${bitmap.height}-${minX},${minY}-${width}x${height}`;
};
export const getCachedOutlinePath = (bitmap: ImageData, cacheKey: string): Array<{
  x: number;
  y: number;
}> => {
  if (outlinePathCache.has(cacheKey)) return outlinePathCache.get(cacheKey)!;
  const outlinePath = generateOptimizedOutlinePath(bitmap);
  outlinePathCache.set(cacheKey, outlinePath);
  if (outlinePathCache.size > MAX_OUTLINE_PATH_CACHE_SIZE) {
    const firstKey = outlinePathCache.keys().next().value;
    outlinePathCache.delete(firstKey);
  }
  return outlinePath;
};
export const invalidateOutlineCache = (labelTitleOrPattern?: string) => {
  if (!labelTitleOrPattern) {
    outlinePathCache.clear();
    return;
  }
  const keysToDelete: string[] = [];
  outlinePathCache.forEach((_, key) => {
    if (key.startsWith(labelTitleOrPattern + '::')) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => outlinePathCache.delete(key));
};
export const generateOptimizedOutlinePath = (bitmap: ImageData): Array<{
  x: number;
  y: number;
}> => {
  const visited = new Set<string>();
  const edgePoints: Array<{
    x: number;
    y: number;
  }> = [];
  const startPoints = findBoundaryStartPoints(bitmap);
  for (const startPoint of startPoints) {
    if (!visited.has(`${startPoint.x},${startPoint.y}`)) {
      traceContourFrom(bitmap, startPoint.x, startPoint.y, visited, edgePoints);
    }
  }
  return edgePoints;
};
export const drawOptimizedOutline = (ctx: CanvasRenderingContext2D, outlinePath: Array<{
  x: number;
  y: number;
}>, strokeColor: string): void => {
  const outlineWidth = getAnnotationOutlineWidth();
  ctx.lineWidth = outlineWidth;

  // Extract RGB values from strokeColor and apply our outline alpha
  const colorMatch = strokeColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (colorMatch) {
    const [, r, g, b] = colorMatch;
    const alpha = getAnnotationOutlineAlpha() / 255;
    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    ctx.strokeStyle = strokeColor;
  }
  drawPathFromPoints(ctx, outlinePath);
};
const drawPathFromPoints = (ctx: CanvasRenderingContext2D, points: Array<{
  x: number;
  y: number;
}>): void => {
  if (points.length === 0) return;
  ctx.beginPath();
  const sortedPoints = points.sort((a, b) => a.y === b.y ? a.x - b.x : a.y - b.y);
  let i = 0;
  while (i < sortedPoints.length) {
    const startPoint = sortedPoints[i];
    let endPoint = startPoint;
    while (i + 1 < sortedPoints.length &&
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    sortedPoints[i + 1].y === startPoint.y &&
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    sortedPoints[i + 1].x === endPoint.x + 1) {
      i++;
      endPoint = sortedPoints[i];
    }

    // @ts-expect-error todo(ts-migration) TS18048 'startPoint' is possibly 'undefined'.
    if (startPoint.x === endPoint.x) {
      // @ts-expect-error todo(ts-migration) TS18048 'startPoint' is possibly 'undefined'.
      ctx.rect(startPoint.x, startPoint.y, 1, 1);
    } else {
      // @ts-expect-error todo(ts-migration) TS18048 'startPoint' is possibly 'undefined'.
      ctx.rect(startPoint.x, startPoint.y, endPoint.x - startPoint.x + 1, 1);
    }
    i++;
  }
  ctx.stroke();
};
export const drawSelectionOutlines = (ctx: CanvasRenderingContext2D, selectedAnnotationIds: string[], semanticAnnotations: Record<string, any>, annotationBitmaps: Record<string, ImageData>) => {
  if (selectedAnnotationIds.length === 0) return;
  ctx.save();
  selectedAnnotationIds.forEach(id => {
    const annotation = semanticAnnotations[id];
    if (!annotation) return;
    const title = annotation.annotationLabel.title;
    const bitmap = annotationBitmaps[title];
    if (!bitmap) return;

    // Compute a stable hash based on bitmap content
    const contentHash = generateBitmapContentHash(bitmap);
    const cacheKey = `${title}::${contentHash}`;

    // Use boundary tracing + caching
    const outlinePath = getCachedOutlinePath(bitmap, cacheKey);
    if (outlinePath.length > 0) {
      drawOptimizedOutline(ctx, outlinePath, annotation.annotationLabel.style.strokeColor);
    }
  });
  ctx.restore();
};
export const getOptimizedCentroid = (bitmap: ImageData): {
  x: number;
  y: number;
} | null => {
  const {
    width,
    height,
    data
  } = bitmap;
  const totalPixels = width * height;

  // For small images, use full precision to avoid missing details
  if (totalPixels < CENTROID_SMALL_IMAGE_THRESHOLD) {
    return calculateExactCentroid(bitmap);
  }

  // For large images, use optimized sampling
  const earlyExitSampleRate = totalPixels < CENTROID_SMALL_IMAGE_THRESHOLD * 4 ? CENTROID_SMALL_IMAGE_EARLY_EXIT_RATE : CENTROID_EARLY_EXIT_SAMPLE_RATE;

  // Early exit for empty bitmap (all alpha = 0)
  let nonZeroAlpha = 0;
  for (let i = 3; i < data.length; i += 4 * earlyExitSampleRate) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (data[i] > 0) {
      nonZeroAlpha++;
      break;
    }
  }
  if (nonZeroAlpha === 0) return null;

  // Estimate density from sampled alpha values
  let alphaSampleCount = 0;
  for (let i = 3; i < data.length; i += 4 * CENTROID_DENSITY_SAMPLE_RATE) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (data[i] > 0) alphaSampleCount++;
  }
  const estimatedDensity = alphaSampleCount / (totalPixels / CENTROID_DENSITY_SAMPLE_RATE);
  const sampleRate = estimatedDensity > CENTROID_DENSITY_THRESHOLD ? CENTROID_HIGH_DENSITY_SAMPLE_RATE : CENTROID_LOW_DENSITY_SAMPLE_RATE;

  // Centroid calculation (alpha channel only)
  let totalX = 0;
  let totalY = 0;
  let count = 0;
  for (let i = 3; i < data.length; i += 4 * sampleRate) {
    const alpha = data[i];
    // @ts-expect-error todo(ts-migration) TS18048 'alpha' is possibly 'undefined'.
    if (alpha > 0) {
      const pixelIndex = (i - 3) / 4;
      const x = pixelIndex % width;
      const y = Math.floor(pixelIndex / width);
      totalX += x;
      totalY += y;
      count++;
    }
  }
  if (count === 0) return null;
  return {
    x: Math.min(width - 1, Math.max(0, Math.round(totalX / count))),
    y: Math.min(height - 1, Math.max(0, Math.round(totalY / count)))
  };
};
const calculateExactCentroid = (bitmap: ImageData): {
  x: number;
  y: number;
} | null => {
  const {
    width,
    height,
    data
  } = bitmap;
  let totalX = 0;
  let totalY = 0;
  let count = 0;

  // Full precision for small images
  for (let i = 3; i < data.length; i += 4) {
    const alpha = data[i];
    // @ts-expect-error todo(ts-migration) TS18048 'alpha' is possibly 'undefined'.
    if (alpha > 0) {
      const pixelIndex = (i - 3) / 4;
      const x = pixelIndex % width;
      const y = Math.floor(pixelIndex / width);
      totalX += x;
      totalY += y;
      count++;
    }
  }
  if (count === 0) return null;
  return {
    x: Math.min(width - 1, Math.max(0, Math.round(totalX / count))),
    y: Math.min(height - 1, Math.max(0, Math.round(totalY / count)))
  };
};
export const isEmptyAnnotation = (imageData: ImageData) => {
  return imageData.data.every(value => value === 0);
};
export const convertPointsToSVGPath = (points: Array<{
  x: number;
  y: number;
}>): string => {
  if (points.length === 0) return '';

  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  let pathData = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    pathData += ` L${points[i].x},${points[i].y}`;
  }
  pathData += ' Z';
  return pathData;
};
export function buildIdBuffer(width: number, height: number, annotationBitmaps: Record<string, ImageData>): AnnotationPixelMap {
  const titleToId: Record<string, number> = {};
  const idToTitle: string[] = [''];
  const orderedTitles = Object.keys(annotationBitmaps);
  let nextId = 1;
  for (const title of orderedTitles) {
    if (!titleToId[title]) {
      titleToId[title] = nextId;
      idToTitle[nextId] = title;
      nextId++;
    }
  }
  const getBitmapId = (x: number, y: number): number | null => {
    if (x < 0 || y < 0 || x >= width || y >= height) return 0;
    for (const title of orderedTitles) {
      const bitmap = annotationBitmaps[title];
      if (!bitmap) continue;
      const imgId = (y * width + x) * 4;
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      if (bitmap.data[imgId + 3] > 0) {
        // @ts-expect-error todo(ts-migration) TS2322 Type 'number | undefined' is not assignable to type 'number | null'.
        return titleToId[title];
      }
    }
    return null;
  };
  return {
    width,
    height,
    titleToId,
    idToTitle,
    getBitmapId
  };
}
export function pickIdAt(x: number, y: number, idb: AnnotationPixelMap): number | null {
  const ix = x | 0,
    iy = y | 0;
  return idb.getBitmapId(ix, iy);
}
export function createSegmentPreviewCanvas(width: number, height: number): HTMLCanvasElement {
  const previewCanvas = document.createElement('canvas');
  previewCanvas.width = width;
  previewCanvas.height = height;
  return previewCanvas;
}
export function createColoredSegment(sourceData: ImageData, segmentColor: string): ImageData {
  const coloredSegment = new ImageData(new Uint8ClampedArray(sourceData.data), sourceData.width, sourceData.height);
  applyColorToImageData(coloredSegment, segmentColor);
  return coloredSegment;
}
export function cleanupCanvas(canvas: HTMLCanvasElement | null) {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}
export function findTranslatedSegmentData(segmentCanvas: HTMLCanvasElement, offsetX: number, offsetY: number, targetWidth: number, targetHeight: number): ImageData {
  const resultCanvas = document.createElement('canvas');
  try {
    resultCanvas.width = targetWidth;
    resultCanvas.height = targetHeight;
    const ctx = resultCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    ctx.clearRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(segmentCanvas, offsetX | 0, offsetY | 0);
    return ctx.getImageData(0, 0, targetWidth, targetHeight);
  } finally {
    cleanupCanvas(resultCanvas);
  }
}
export const loadAnnotationBitmaps = async (annotations: Record<string, BrushAnnotationT>, applyColor = false): Promise<Record<string, ImageData>> => {
  const entries = Object.values(annotations);
  const decoded = await Promise.all(entries.map(annotation => base64ToImageData(annotation.data.base64String, preProcessSAMPixelData).then(imageData => ({
    imageData,
    annotation
  }))));
  const bitmaps: Record<string, ImageData> = {};
  for (const {
    imageData,
    annotation
  } of decoded) {
    if (!imageData) continue;
    applyColor && applyColorToImageData(imageData, annotation.annotationLabel.style.fillColor || annotation.annotationLabel.style.strokeColor);
    const title = annotation.annotationLabel.title;
    const existing = bitmaps[title];
    if (existing) {
      for (let i = 0; i < imageData.data.length; i += 4) {
        if ((imageData.data[i + 3] ?? 0) > 0) {
          existing.data[i] = imageData.data[i] ?? 0;
          existing.data[i + 1] = imageData.data[i + 1] ?? 0;
          existing.data[i + 2] = imageData.data[i + 2] ?? 0;
          existing.data[i + 3] = imageData.data[i + 3] ?? 0;
        }
      }
    } else {
      bitmaps[title] = imageData;
    }
  }
  return bitmaps;
};
export const filterSegmentationAnnotations = (annotationStore: Record<string, AnnotationT> | undefined, annotationMLStore?: Record<string, AnnotationT> | undefined, annotationConsensusStore?: Record<string, AnnotationT> | undefined): Record<string, BrushAnnotationT> => {
  const segmentations: Record<string, BrushAnnotationT> = {};
  if (annotationStore) {
    Object.keys(annotationStore).forEach(annotationId => {
      const annotation = annotationStore[annotationId];
      if (!annotation?.hidden && annotation?.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
        segmentations[annotationId] = (annotation as BrushAnnotationT);
      }
    });
  }
  if (annotationMLStore) {
    Object.keys(annotationMLStore).forEach(annotationId => {
      const annotation = annotationMLStore[annotationId];
      if (annotation?.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
        segmentations[annotationId] = (annotation as BrushAnnotationT);
      }
    });
  }
  if (annotationConsensusStore) {
    Object.keys(annotationConsensusStore).forEach(annotationId => {
      const annotation = annotationConsensusStore[annotationId];
      if (!annotation?.hidden && annotation?.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
        segmentations[annotationId] = (annotation as BrushAnnotationT);
      }
    });
  }
  return segmentations;
};
export const transformReviewAnnotations = (annotationUnderUserReview: Record<string, AnnotationUnderUserReviewT> | undefined, isSAMSegmentAnnotation: (annotation: any) => boolean): Record<string, BrushAnnotationT> => {
  const reviewAnnotations: Record<string, BrushAnnotationT> = {};
  if (!annotationUnderUserReview) return reviewAnnotations;
  Object.keys(annotationUnderUserReview).forEach(annotationId => {
    const annotation = annotationUnderUserReview[annotationId];
    if (!isSAMSegmentAnnotation(annotation) || !annotation?.annotationData) {
      return;
    }
    const activeLabelIdx = annotation.annotationMetaData?.length || 0;
    const annotationLabel = annotation.annotationMetaData?.[activeLabelIdx - 1]?.annotationLabel;
    if (!annotationLabel) return;
    const annotationData = (annotation.annotationData?.[0] as BrushDataT);
    if (!annotationData) return;
    reviewAnnotations[annotationId] = {
      data: annotationData,
      annotationType: ANNOTATION_TYPE.SEGMENTATION,
      annotationLabel
    };
  });
  return reviewAnnotations;
};